import type { TestSingleChoiceSchemaType } from '@vedalib/editor/lib/brand'
import React, { useCallback, useId } from 'react'

import type { ElementStyleCss } from 'services/Branding/types'
import { testPropsEl } from 'utils/test/qaData'

import LabelStyled from '../../styled/LabelStyled'
import MarkStyled from '../../styled/MarkStyled'
import RadioLabelStyled from '../../styled/RadioLabelStyled'

interface IRadioProps {
  disabled?: boolean
  value: string
  checked: boolean
  onChange?: (value: string) => void
  setIsValid?: (value: boolean) => void
  styles: ElementStyleCss<TestSingleChoiceSchemaType>
  testData: string
  isEditor?: boolean
  children: React.ReactNode
  active?: boolean
  name?: string
}

const Radio: React.FC<IRadioProps> = ({
  disabled,
  value,
  checked,
  onChange,
  setIsValid,
  children,
  styles,
  testData,
  isEditor,
  active,
  name,
}) => {
  const id = useId()
  const handleOnChange = useCallback(() => onChange?.(value), [value, onChange])
  const handleSetIsValid = useCallback(() => setIsValid?.(!checked), [checked, setIsValid])

  return (
    <RadioLabelStyled
      {...testPropsEl('courseRadio', { value }, { checked, testData })}
      $disabled={disabled}
      $styles={styles}
      htmlFor={id}
    >
      <input
        checked={checked}
        disabled={disabled}
        id={isEditor ? undefined : id}
        name={name}
        onChange={handleOnChange}
        tabIndex={active ? 0 : -1}
        type='radio'
      />
      <MarkStyled
        {...testPropsEl('checkmarkRadio', { value }, { checked, testData })}
        $checked={checked}
        $disabled={disabled}
        $styles={styles}
        aria-label={isEditor ? `Mark as ${checked ? 'incorrect' : 'correct'} answer` : undefined}
        onClick={isEditor ? handleSetIsValid : undefined}
        onKeyDown={
          isEditor
            ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleSetIsValid()
                }
              }
            : undefined
        }
        role={isEditor ? 'button' : undefined}
        tabIndex={isEditor ? 0 : undefined}
      />
      <LabelStyled $styles={styles}>{children}</LabelStyled>
    </RadioLabelStyled>
  )
}

export default Radio
